import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const rootSx: SxProps<Theme> = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

export const heroSx: SxProps<Theme> = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 3,
    padding: {xs: 3, md: 4},
    color: theme.palette.common.white,
    background: `linear-gradient(135deg, ${theme.palette.brand.dark} 0%, ${theme.palette.brand.main} 45%, ${theme.palette.primary.main} 100%)`,
    boxShadow: `0 12px 32px ${alpha(theme.palette.brand.main, 0.25)}`,
    // Content-sized below md, where the amount and the meta row already fill the hero and the
    // fixed floor only added dead space. The 200px desktop hero is unchanged.
    minHeight: {xs: 'auto', md: 200},
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -120,
        right: -120,
        width: 320,
        height: 320,
        borderRadius: '50%',
        background: alpha(theme.palette.common.white, 0.08),
        pointerEvents: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -100,
        left: -80,
        width: 240,
        height: 240,
        borderRadius: '50%',
        background: alpha(theme.palette.common.white, 0.05),
        pointerEvents: 'none',
    },
};

export const heroHeaderRowSx: SxProps<Theme> = {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    mb: 2,
};

export const heroEyebrowSx: SxProps<Theme> = {
    color: alpha(theme.palette.common.white, 0.85),
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
};

export const heroAmountSx: SxProps<Theme> = {
    position: 'relative',
    zIndex: 1,
    color: theme.palette.common.white,
    fontSize: {xs: '2.25rem', md: '3rem'},
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
};

export const heroCurrencySx: SxProps<Theme> = {
    color: alpha(theme.palette.common.white, 0.7),
    fontSize: {xs: '1.125rem', md: '1.5rem'},
    fontWeight: 500,
    ml: 1,
};

export const heroMetaRowSx: SxProps<Theme> = {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: {xs: 1.5, md: 3},
    mt: 3,
    color: alpha(theme.palette.common.white, 0.9),
};

export const heroMetaItemSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.875rem',
    fontWeight: 500,
    '& svg': {
        fontSize: '1.1rem',
        opacity: 0.85,
    },
};

export const statusChipSx: SxProps<Theme> = {
    background: alpha(theme.palette.common.white, 0.95),
    borderRadius: 999,
    padding: theme.spacing(0.5, 1.5),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
    '& > .MuiTypography-root': {
        background: 'transparent !important',
        padding: '0 !important',
    },
};

export const statsRowSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
    gap: 2,
};

export const statCardSx: SxProps<Theme> = {
    background: theme.palette.background.paper,
    borderRadius: 2,
    padding: 2.5,
    border: `1px solid ${theme.palette.neutral[100]}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px ${alpha(theme.palette.neutral[900], 0.06)}`,
        borderColor: theme.palette.neutral[200],
    },
};

export const statIconWrapperSx = (color: string): SxProps<Theme> => ({
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: '50%',
    background: alpha(color, 0.12),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color,
    '& svg': {fontSize: '1.4rem'},
});

export const statBodySx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    minWidth: 0,
    flex: 1,
};

export const statLabelSx: SxProps<Theme> = {
    color: theme.palette.neutral[500],
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
};

export const statValueSx: SxProps<Theme> = {
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const footerSx: SxProps<Theme> = {
    background: theme.palette.neutral[50],
    borderRadius: 2,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
};

export const footerItemSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.8125rem',
    color: theme.palette.neutral[600],
    fontWeight: 500,
    '& svg': {
        fontSize: '1.05rem',
        color: theme.palette.neutral[400],
    },
};

export const syncBadgeSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    background: alpha(theme.palette.success.main, 0.12),
    color: theme.palette.success.dark,
    borderRadius: 999,
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    fontWeight: 600,
    '& svg': {
        fontSize: '0.9rem',
    },
};
